import axios, { type AxiosResponse, type Method } from "axios";
import { http } from '@/http';
import { computed, onBeforeUnmount, onMounted, type Ref, ref, toValue, watch, type MaybeRefOrGetter } from "vue";
import { encodeForSend, isPlainObject, recursiveObjectPromiseAll } from '@common/utils';
import { useEventListener, useMutationObserver } from "@vueuse/core";
import debounce from "lodash-es/debounce";
import type { PfForm } from "@vue-patternfly/core";
import { useManagedProp } from "./use";
import type { ComponentExposed } from "vue-component-type-helpers";

function getParentTabInForm(el: Element) {
  const panel = el.closest('form [role=tabpanel]');
  if (!panel) {
    return null;
  }

  const panels = panel.parentElement?.children ? [...panel.parentElement.children] : [];
  const num = panels.indexOf(panel);

  return panel.parentElement?.previousElementSibling?.querySelector(`:scope > :nth-child(${num + 1}) > [role=tab]`);
}

type FormInputElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function isFormElement(e: any): e is FormInputElement {
  return e instanceof HTMLInputElement || e instanceof HTMLSelectElement || e instanceof HTMLTextAreaElement;
}

function refreshErrorBadgesInTabs(form: HTMLFormElement) {
  const tabErrorMap = new WeakMap<Element, {
    elements: FormInputElement[],
    errors: number,
  }>();

  // conto gli errori per ogni tab (se) presente
  const tabs = [];
  for (const el of form.elements) {
    if (!isFormElement(el)) {
      continue;
    }

    let tab = getParentTabInForm(el);
    while (tab) {
      tabs.push(tab);
      const tabInfo = tabErrorMap.get(tab) ?? {
        elements: [],
        errors: 0,
      };

      if (!el.validity?.valid) {
        tabInfo.elements.push(el);
        tabInfo.errors++;
      }

      tabErrorMap.set(tab, tabInfo);
      tab = getParentTabInForm(tab);
    }
  }

  // aggiungo/rimuovo/aggiorno i badge all'interno delle tab manipolando manualmente il dom
  for (const tab of tabs) {
    const tabInfo = tabErrorMap.get(tab);
    if (!tabInfo) {
      continue;
    }

    let badge = tab.querySelector(':scope > .badge-danger');
    if (tabInfo.errors) {
      if (!badge) {
        badge = document.createElement('span');
        badge.classList.add('badge');
        badge.classList.add('badge-danger');
        tab.appendChild(badge);
      }
      badge.innerHTML = tabInfo.errors.toString();
      badge.setAttribute('title', `Contiene ${tabInfo.errors} errori`);
    } else if (badge) {
      tab.removeChild(badge);
    }
  }
}

/**
 * Composable per gestire form con validazione avanzata, submit XHR/nativo, e gestione errori
 * @template ResponseData - Tipo della risposta del server dopo il submit
 * @param options - Opzioni di configurazione del form
 * @param options.method - Metodo HTTP per il submit (default: 'post')
 * @param options.disabled - Se il form è disabilitato
 * @param options.xhr - Se usare submit XHR invece di submit nativo (default: true)
 * @param options.preventSubmit - Se prevenire il submit del form
 * @param options.action - URL dell'endpoint per il submit
 * @param options.confirmMessage - Messaggio di conferma da mostrare prima del submit
 * @param options.encoding - Formato di encoding: 'form' (FormData) o 'json' (default: FormData convertito a JSON)
 * @param options.additionalData - Dati extra da aggiungere al payload
 * @param options.onBeforeSubmit - Callback pre-submit, return false per annullare
 * @param options.onSubmit - Callback post-submit con i dati della risposta
 * @param options.onCheckValidity - Callback chiamato ad ogni check di validità
 * @returns Oggetto con ref, stato e metodi per gestire il form
 */
export function useXForm<ResponseData = any>(
  {
    method,
    disabled,
    xhr,
    preventSubmit,
    action,
    confirmMessage,
    encoding,
    additionalData,
    onBeforeSubmit,
    onSubmit,
    onCheckValidity,
  }: {
    method: MaybeRefOrGetter<Method>;
    disabled?: MaybeRefOrGetter<boolean | undefined>;
    xhr?: MaybeRefOrGetter<boolean | undefined>;
    preventSubmit?: MaybeRefOrGetter<boolean | undefined>;
    action?: MaybeRefOrGetter<string | undefined>;
    confirmMessage?: MaybeRefOrGetter<string | undefined>;
    encoding?: MaybeRefOrGetter<'form' | 'json' | undefined>;
    sending?: MaybeRefOrGetter<boolean | undefined>;
    additionalData?: Record<string, any> | (() => Record<string, any>) | undefined;
    onBeforeSubmit?: () => boolean | undefined | void;
    onSubmit?: (e: Event | undefined, responseData?: ResponseData) => MaybePromise<void>;
    onCheckValidity?: (v: boolean) => void;
  } = {
      method: 'post',
      disabled: false,
      xhr: true,
      preventSubmit: false,
      sending: undefined,
    },
) {
  const sending = useManagedProp('sending', false);
  const formRef: Ref<HTMLFormElement | HTMLDivElement | ComponentExposed<typeof PfForm> | null> = ref(null);
  const fieldset: Ref<HTMLFieldSetElement | null> = ref(null);
  const form = computed(() => {
    if (formRef.value instanceof HTMLElement) {
      return formRef.value;
    }
    return formRef.value?.$el as HTMLFormElement | null;
  });

  const effectiveDisabled = computed(() => toValue(disabled) || sending.value);

  let disabledMap: WeakMap<FormInputElement, boolean> | null = null;

  watch([effectiveDisabled], () => {
    const elements = () => [...fieldset.value?.elements ?? []].filter(isFormElement);

    if (effectiveDisabled.value) {
      disabledMap = new WeakMap();
      for (const input of elements()) {
        if (typeof input.disabled !== 'undefined' && !input.disabled) {
          disabledMap.set(input, input.disabled);
          input.disabled = true;
        }
      }
    } else if (disabledMap) {
      for (const input of elements()) {
        if (disabledMap.has(input)) {
          input.disabled = disabledMap.get(input) ?? false;
        }
      }
      disabledMap = null;
    }
  });

  const valid = ref(false);

  const checkValidity = () => {
    const formContainer: HTMLFormElement | HTMLFieldSetElement | null = form.value instanceof HTMLFormElement ? form.value : fieldset.value;
    if (formContainer) {
      valid.value = [...formContainer.elements].every(e => isFormElement(e) ? e.checkValidity() : true);
    } else {
      valid.value = false;
    }
    onCheckValidity?.(valid.value);
    return valid.value;
  };

  const inputSet = new WeakSet<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>();

  const watchEdits = debounce(() => {
    for (const input of fieldset.value?.querySelectorAll('input, select, textarea') ?? []) {

      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement)) {
        continue;
      }

      if (!inputSet.has(input)) {
        if (!(input instanceof HTMLInputElement) || !['checkbox', 'radio'].includes(input.type)) {
          useEventListener(input, 'input', checkValidity);
        }
        useEventListener(input, 'change', checkValidity);
        input.checkValidity();
        inputSet.add(input);
      }
    }
    checkValidity();
  }, 500);

  useMutationObserver(fieldset, watchEdits, {
    subtree: true,
    childList: true,
  });

  onMounted(() => {
    watchEdits();
    if (fieldset.value?.checkValidity === HTMLFieldSetElement.prototype.checkValidity) {
      fieldset.value.checkValidity = checkValidity;
    }
    if (form.value instanceof HTMLFormElement && form.value?.checkValidity === HTMLFormElement.prototype.checkValidity) {
      form.value.checkValidity = checkValidity;
    }
  });

  onBeforeUnmount(() => {
    if (fieldset.value) {
      fieldset.value.checkValidity = HTMLFieldSetElement.prototype.checkValidity;
    }
    if (form.value instanceof HTMLFormElement) {
      form.value.checkValidity = HTMLFormElement.prototype.checkValidity;
    }
  });

  const submitErrors: Ref<AxiosResponse[]> = ref([]);

  async function encode() {
    if (!fieldset.value) {
      return;
    }

    let data;
    if (form.value instanceof HTMLFormElement) {
      data = new FormData(form.value);
    } else {
      data = new FormData();
      for (const el of fieldset.value.elements) {
        if ((el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) && el.name) {
          data.append(el.name, el.value);
        }
      }
    }

    const extraData = typeof additionalData === 'object' ? additionalData : (additionalData?.() ?? {});

    if (toValue(encoding) == 'form') {
      for (const [k, v] of Object.entries(extraData)) {
        if (v && typeof v[Symbol.iterator] === 'function') {
          const name = `${k}[]`;
          for (const item of v) {
            if (typeof item === 'object' && !(item instanceof Blob)) {
              data.append(name, JSON.stringify(item));
            } else {
              data.append(name, item as any);
            }
          }
        } else {
          data.append(k, v);
        }
      }
      return data;
    }

    let jsonData: Record<string, any> = {};
    for (const [k, v] of data) {
      let encV: any = v;
      const inputElement = fieldset.value.elements.namedItem(k);
      if (inputElement instanceof HTMLInputElement && inputElement.type === 'checkbox') {
        encV = Boolean(encV);
      }
      encV = encodeForSend(encV);

      if (k.substring(k.length - 2) == '[]') {
        const arrK = k.substring(0, k.length - 2);
        if (!Object.hasOwnProperty.call(jsonData, arrK)) {
          jsonData[arrK] = [];
        }
        jsonData[arrK].push(encV);
      } else {
        jsonData[k] = encV;
      }
    }

    for (const [k, v] of Object.entries(extraData)) {
      if (v && typeof v[Symbol.iterator] === 'function') {
        jsonData[k] = [];
        for (const item of v) {
          if (typeof item === 'object' && !isPlainObject(item)) {
            jsonData[k].push(encodeForSend(item));
          } else {
            jsonData[k].push(item);
          }
        }
      } else if (typeof v === 'object' && !isPlainObject(v)) {
        jsonData[k] = encodeForSend(v);
      } else {
        jsonData[k] = v;
      }
    }

    jsonData = await recursiveObjectPromiseAll(jsonData);
    return JSON.stringify(jsonData);
  }

  function submit(e: Event | undefined) {
    const prevent = e instanceof Event ? () => e.preventDefault() : () => { return; };
    const message = toValue(confirmMessage);

    if (sending.value || toValue(preventSubmit) || (message && !confirm(message))) {
      prevent();
      return false;
    }

    if (!(form.value instanceof HTMLFormElement)) {
      return;
    }

    if (!form.value.reportValidity()) {
      prevent();
      refreshErrorBadgesInTabs(form.value);
      return false;
    }

    if (onBeforeSubmit?.() === false) {
      prevent();
      return false;
    }

    if (!toValue(action)) {
      prevent();

      sending.value = true;
      (async () => {
        try {
          await onSubmit?.(e);
        } finally {
          sending.value = false;
        }
      })();

      return false;
    }

    if (!toValue(xhr)) {
      return true;
    }

    prevent();

    (async () => {
      // important! form data needs to be read before setting sending=true which in turn disables the form, making FormData empty.
      const data = await encode();

      sending.value = true;

      try {
        const headers: Record<string, string> = {};
        if (toValue(encoding) == 'json') {
          headers['Content-Type'] = 'application/json';
        }
        const response = await http.request<ResponseData>({
          method: toValue(method),
          url: toValue(action),
          headers,
          data,
        });

        if (Number((response.data as any)?.result) >= 1) {
          if ((response.data as any)?.message) {
            submitErrors.value.push(response);
          }
          return;
        }

        await onSubmit?.(e, response.data);
      } catch (ex: any) {
        if (!axios.isAxiosError(ex) || !ex.response) {
          throw ex;
        }
        submitErrors.value.push(ex.response);
      } finally {
        sending.value = false;
      }
    })();

    return false;
  }

  function validate() {
    if (!valid.value) {
      fieldset.value?.reportValidity();
    }
  }

  return {
    /** Computed che restituisce l'elemento HTMLFormElement o HTMLDivElement del form */
    form,
    /** Template ref al form (HTMLFormElement, HTMLDivElement o PfForm) */
    formRef,
    /** Template ref al fieldset principale del form */
    fieldset,
    /** Funzione per codificare i dati del form in FormData o JSON string */
    encode,
    /** Ref reattivo che indica se il form è in fase di invio */
    sending,
    /** Computed che indica se il form è disabilitato (disabled prop o sending) */
    effectiveDisabled,
    /** Ref con array di risposte di errore dal server (AxiosResponse[]) */
    submitErrors,
    /** Ref reattivo che indica se il form è valido */
    valid,
    /** Funzione per verificare la validità di tutti i campi del form */
    checkValidity,
    /** Funzione handler per il submit del form (gestisce validazione, encoding, XHR) */
    submit,
    /** Funzione per triggare la validazione e mostrare errori all'utente */
    validate,
  };
}
